'use client'

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion'
import { Button } from '@/components/ui/button'
import { useIsMobileClient } from '@/hooks/common/use-device-type'
import { cn } from '@/lib/utils'
import { TCollectionFormatted, TFilterData } from '@/types/collection'
import { useMemo } from 'react'
import { applyFilter, getFilterTags } from '../../utils/filter-tag-utils'
import { FilterBarTags } from './filter-bar-tags'
import { FilterCheckbox } from './partials/filter-checkbox'
import { FilterColors } from './partials/filter-colors'
import { FilterItemAccordion } from './partials/filter-item-accordion'
import { FilterOutlet } from './partials/filter-oulet'
import { FilterPriceRange } from './partials/filter-price-range'
import { FilterRadio } from './partials/filter-radio'
import { FilterSizes } from './partials/filter-sizes'
import { FilterUnderwearStyle } from './partials/filter-underwear-style'
import { useFilterState } from './use-filter-state'

type Props = {
  className?: string
  filter: TFilterData
  collection: TCollectionFormatted
  onClose?: () => void
}

export const FilterList: React.FC<Props> = ({
  className,
  filter,
  collection,
  onClose,
}) => {
  const isMobile = useIsMobileClient()

  const {
    localFilter,
    changeFilter,
    applyFilter: applyLocalFilter,
    getFilterValue,
    getFilterValues,
    setLocalFilter,
  } = useFilterState({
    onClose,
    collection,
  })

  const isUnderWare = collection.filter.isUnderwearCollection
  const isOutletCollection = collection.filter.isOutletCollection

  // Calculate default open sections
  const defaultOpenSections = useMemo(() => {
    const defaults = ['item-2', 'item-3', 'item-4'] // Size, color and price by default

    // Add attribute sections if they exist
    if (filter?.attributeCards) {
      const firstAttributeCard = filter.attributeCards[0]
      if (firstAttributeCard) {
        defaults.push(`attr-${firstAttributeCard.seo_alias}`)
      }
    }

    return defaults
  }, [filter])

  // Function to render collection attribute filters
  const renderAttributeFilters = () => {
    const normalAttributes = filter?.normalAttributes || []

    return normalAttributes.map((attribute, index) => (
      <AccordionItem
        key={attribute.seo_alias}
        value={`attr-${index}`}
        className='border-b border-b-neutral-200 pb-4.5 lg:border-0 lg:p-0 lg:pb-0'
      >
        <AccordionTrigger className='py-1.5 font-sans text-sm font-medium leading-4.5 text-neutral-400 lg:py-3'>
          {attribute.title}
        </AccordionTrigger>
        <AccordionContent className='py-1.5 lg:pt-3'>
          <FilterCheckbox
            items={attribute.items.map((item) => ({
              value: item.seo_alias,
              label: item.title,
            }))}
            onFilterChange={(values: string[]) =>
              changeFilter(attribute.seo_alias, values)
            }
            selectedValues={getFilterValues(attribute.seo_alias)}
          />
        </AccordionContent>
      </AccordionItem>
    ))
  }

  const renderAttributeCards = () => {
    return filter?.attributeCards?.map((card) => (
      <FilterItemAccordion
        key={card.seo_alias}
        title={card.title}
        value={`attr-${card.seo_alias}`}
      >
        <FilterRadio
          value={getFilterValue(card.seo_alias) || ''}
          onFilterChange={(value) => changeFilter(card.seo_alias, value)}
          items={card.items.map((item) => ({
            value: item.seo_alias,
            label: item.title,
          }))}
        />
      </FilterItemAccordion>
    ))
  }

  return (
    <div
      className={cn(
        'relative flex flex-col gap-6 overflow-y-auto overflow-x-hidden pb-36 pr-2 max-lg:max-h-[calc(100vh-50px)]',
        className
      )}
    >
      {isMobile && (
        <FilterBarTags
          tags={getFilterTags(localFilter, collection)}
          onRemoveTag={(tag) => setLocalFilter(applyFilter(localFilter, tag))}
        />
      )}

      <Accordion
        type='multiple'
        className='flex w-full flex-col gap-4.5 lg:gap-2'
        defaultValue={defaultOpenSections}
      >
        {/* Filter attribute cards */}
        {filter?.attributeCards &&
          filter.attributeCards.length > 0 &&
          renderAttributeCards()}

        {isUnderWare && filter.underwearList && (
          <FilterItemAccordion
            key='kieu-dang-quan-lot'
            title='Kiểu dáng'
            value='kieu-dang-quan-lot'
          >
            <FilterUnderwearStyle
              onFilterChange={(value) =>
                changeFilter('kieu-dang-quan-lot', value)
              }
              value={getFilterValue('kieu-dang-quan-lot') || ''}
              underwearList={filter.underwearList}
            />
          </FilterItemAccordion>
        )}
        {isOutletCollection && (
          <FilterItemAccordion
            key='discount_percent'
            title='Giảm giá'
            value='discount_percent'
          >
            <FilterOutlet
              value={getFilterValue('discount_percent') || ''}
              onFilterChange={(value) =>
                changeFilter('discount_percent', value)
              }
            />
          </FilterItemAccordion>
        )}
        {/* Show size filter if available */}
        {filter?.showSizeFilter && (
          <AccordionItem
            value='item-2'
            className='border-b border-b-neutral-200 pb-4.5 lg:border-0 lg:p-0 lg:pb-0'
          >
            <AccordionTrigger className='py-1.5 font-sans text-sm font-medium leading-4.5 text-neutral-400 lg:py-3'>
              Kích cỡ
            </AccordionTrigger>
            <AccordionContent className='py-1.5 lg:pt-3'>
              <FilterSizes
                onFilterChange={(value) => changeFilter('size', value)}
                defaultValue={getFilterValues('size')}
                sizeList={collection?.variantInfo?.sizeList ?? []}
              />
            </AccordionContent>
          </AccordionItem>
        )}

        {/* Show color filter if available */}
        {filter?.showColorFilter && (
          <AccordionItem
            value='item-3'
            className='border-b border-b-neutral-200 pb-4.5 lg:border-0 lg:p-0 lg:pb-0'
          >
            <AccordionTrigger className='py-1.5 font-sans text-sm font-medium leading-4.5 text-neutral-400 lg:py-3'>
              Màu sắc
            </AccordionTrigger>
            <AccordionContent className='py-1.5 lg:pt-3'>
              <FilterColors
                onFilterChange={(value) => changeFilter('color', value)}
                value={getFilterValue('color')}
                colorsMapped={collection?.variantInfo?.colorMapperList ?? []}
              />
            </AccordionContent>
          </AccordionItem>
        )}
        {/* Show attribute filters first */}
        {filter?.normalAttributes &&
          filter.normalAttributes.length > 0 &&
          renderAttributeFilters()}

        {/* Always show price filter */}
        <AccordionItem
          value='item-4'
          className='border-b border-b-neutral-200 pb-4.5 lg:border-0 lg:p-0 lg:pb-0'
        >
          <AccordionTrigger className='py-1.5 font-sans text-sm font-medium leading-4.5 text-neutral-400 lg:py-3'>
            Giá
          </AccordionTrigger>
          <AccordionContent className='py-1.5 lg:pt-3'>
            <FilterPriceRange
              onFilterChange={(values: string[]) =>
                changeFilter('price_range', values)
              }
              value={getFilterValues('price_range')}
            />
          </AccordionContent>
        </AccordionItem>
      </Accordion>
      {isMobile && (
        <div className='fixed bottom-0 left-0 right-0 mt-4 border-t border-t-neutral-200 bg-light p-4'>
          <Button
            variant={'black'}
            className='w-full'
            onClick={() => applyLocalFilter(localFilter)}
          >
            Áp dụng
          </Button>
        </div>
      )}
    </div>
  )
}
